import { BufferGeometry, LineBasicMaterial, LineLoop, Vector3 } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugCircleConfig } from '../types';

export interface AddCircleParams {
	config: DebugCircleConfig;
}

/**
 * Add a debug circle
 */
export const addCircleCfn = (
	self: DebugManager,
	{ config }: AddCircleParams
): LineLoop | null => {
	const { id, center, radius, color = 0x00ff00, segments = 64 } = config;

	// Validate radius - skip if zero, negative, or NaN
	if (!radius || radius <= 0 || !Number.isFinite(radius)) {
		console.warn(`DebugManager.addCircle: Skipping circle '${id}' with invalid radius: ${radius}`);
		return null;
	}

	// Validate center - skip if any coordinate is NaN
	if (!Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(center.z)) {
		console.warn(`DebugManager.addCircle: Skipping circle '${id}' with invalid center: [${center.x}, ${center.y}, ${center.z}]`);
		return null;
	}

	// Remove existing circle with same ID
	if (self.hasCircle(id)) {
		self.removeCircle(id);
	}

	// Create circle points
	const points: Vector3[] = [];
	for (let i = 0; i <= segments; i++) {
		const angle = (i / segments) * Math.PI * 2;
		points.push(new Vector3(
			center.x + Math.cos(angle) * radius,
			center.y,
			center.z + Math.sin(angle) * radius
		));
	}

	// Create geometry
	const geometry = new BufferGeometry().setFromPoints(points);
	const material = new LineBasicMaterial({ color });
	const circle = new LineLoop(geometry, material);
	circle.name = `debug_circle_${id}`;
	circle.visible = self.visible;
	// Mark as interface object to exclude from wireframe mode
	circle.userData.isInterface = true;
	circle.userData.type = 'debugVisualization';

	self.scene.add(circle);
	self.registerCircle(id, circle);

	return circle;
};

export interface UpdateCircleParams {
	id: string;
	center: Vector3;
	radius: number;
}

/**
 * Update a debug circle
 */
export const updateCircleCfn = (
	self: DebugManager,
	{ id, center, radius }: UpdateCircleParams
): void => {
	// For simplicity, just recreate the circle
	const existingCircle = self.getCircle(id);
	if (existingCircle) {
		const material = existingCircle.material as LineBasicMaterial;
		const color = material.color.getHex();
		self.removeCircle(id);
		addCircleCfn(self, { config: { id, center, radius, color } });
	}
};

export interface RemoveCircleParams {
	id: string;
}

/**
 * Remove a debug circle
 */
export const removeCircleCfn = (
	self: DebugManager,
	{ id }: RemoveCircleParams
): void => {
	const circle = self.getCircle(id);
	if (circle) {
		self.scene.remove(circle);
		circle.geometry.dispose();
		(circle.material as LineBasicMaterial).dispose();
		self.unregisterCircle(id);
	}
};
